import React from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { TOASTER_TYPE, ToastMessage } from '../../../components';
import {
    addPayLinkStoreCartItem, amendPayLinkStoreCartItem, getPayLinkStoreCartItems,
    removePayLinkStoreCartItem
} from '../../../services/api';
import { IProductCatalogue } from '../../../types/interfaces';
import { getGatewaySettings } from '../services/api';
import { IStoreProfile } from '../types/campaigns';
import { IGatewaySettings } from '../types/payments';

// type TPayLinkStoreProfile = {
//   customerUniqueReferenceNumber: string;
//   merchantUniqueReferenceNumber: string;
//   storeName?: string;
//   slug?: string;
// };

export type IPayLinkCartItem = IProductCatalogue & {
  quantity: number;
};

type TPayLinkCartContext = {
  sessionUniqueReferenceNumber: string;
  setSessionUniqueReferenceNumber: React.Dispatch<React.SetStateAction<string>>;
  merchantSessionUrn: string;
  cartSessionUrn: string;
  storeProfile: IStoreProfile | null;
  setStoreProfile: (profile: IStoreProfile | null) => void;
  cartItems: IPayLinkCartItem[];
  cartCount: number;
  pendingProductUrns: string[];
  isFetchingCart: boolean;
  addToCart: (product: IProductCatalogue) => void;
  incrementItem: (productUrn: string) => void;
  decrementItem: (productUrn: string) => void;
  removeFromCart: (productUrn: string) => void;
  gatewaySetting: IGatewaySettings | null;
  setGatewaySetting: React.Dispatch<
    React.SetStateAction<IGatewaySettings | null>
  >;
  currency: string;
};

const CART_KEY = "paylink_cart_items";
const STORE_KEY = "paylink_store_profile";
const GLOBAL_SESSION_KEY = "C_S";
export const CHECKOUT_SESSION_KEY = "paylink_c_s";

const PayLinkCartContext = React.createContext<TPayLinkCartContext | null>(
  null,
);

const generateUrn = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateGlobalSessionUrn = () => {
  const existing = localStorage.getItem(GLOBAL_SESSION_KEY);

  if (existing) {
    return existing;
  }

  const generated = generateUrn();
  localStorage.setItem(GLOBAL_SESSION_KEY, generated);
  return generated;
};

const createFreshGlobalSessionUrn = () => {
  const generated = generateUrn();
  localStorage.setItem(GLOBAL_SESSION_KEY, generated);
  return generated;
};

const getOrCreateMerchantSessionUrn = (customerUrn: string) => {
  const existing = localStorage.getItem(customerUrn);

  if (existing) {
    return existing;
  }

  const generated = generateUrn();
  localStorage.setItem(customerUrn, generated);
  return generated;
};

export const PayLinkCartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [storeProfile, setStoreProfileState] =
    React.useState<IStoreProfile | null>(() => {
      try {
        const cached = localStorage.getItem(STORE_KEY);
        return cached ? JSON.parse(cached) : null;
      } catch {
        return null;
      }
    });
  const [gatewaySetting, setGatewaySetting] =
    React.useState<IGatewaySettings | null>(null);
  const [cartItems, setCartItems] = React.useState<IPayLinkCartItem[]>(() => {
    try {
      const cached = localStorage.getItem(CART_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [pendingProductUrns, setPendingProductUrns] = React.useState<string[]>(
    [],
  );
  const [sessionUniqueReferenceNumber, setSessionUniqueReferenceNumber] =
    React.useState<string>("");
  const [merchantSessionUrn, setMerchantSessionUrn] = React.useState<string>(
    "",
  );
  const [cartSessionUrn, setCartSessionUrn] = React.useState<string>(() =>
    getOrCreateGlobalSessionUrn(),
  );
  const { data: remoteCartItems, isLoading: isFetchingCart } = useQuery({
    queryKey: ["paylink-store-cart", cartSessionUrn],
    queryFn: () => getPayLinkStoreCartItems(cartSessionUrn),
    enabled: !!cartSessionUrn,
    refetchOnWindowFocus: false,
  });
  const { data: gateway_data } = useQuery({
    queryKey: ["PayLinkGateway", storeProfile?.customerUniqueReferenceNumber],
    queryFn: () =>
      getGatewaySettings({
        customerUniqueReferenceNumber:
          storeProfile!.customerUniqueReferenceNumber,
      }),
    enabled: !!storeProfile?.customerUniqueReferenceNumber,
    staleTime: Infinity,
  });

  React.useEffect(() => {
    if (gateway_data && gateway_data.length > 0) {
      const _live_gateway_setting =
        gateway_data.find((g) => g.environment === "Live") ?? null;
      setGatewaySetting(_live_gateway_setting);
    }
  }, [gateway_data]);

  React.useEffect(() => {
    const customerUrn = storeProfile?.customerUniqueReferenceNumber;

    if (!customerUrn) {
      return;
    }

    const storedMerchantSessionUrn =
      localStorage.getItem(customerUrn) ?? getOrCreateMerchantSessionUrn(customerUrn);

    setMerchantSessionUrn(storedMerchantSessionUrn);
  }, [storeProfile?.customerUniqueReferenceNumber]);

  React.useEffect(() => {
    if (!remoteCartItems || remoteCartItems.length === 0) {
      return;
    }

    setCartItems((prev) => {
      const existingUrns = new Set(
        prev.map(
          (item) =>
            `${item.productCatalogueItemUniqueReferenceNumber}::${item.variantUniqueReferenceNumber || ""}`,
        ),
      );

      const incoming = remoteCartItems
        .filter(
          (item: any) =>
            !!item?.productCatalogueItemUniqueReferenceNumber &&
            !existingUrns.has(
              `${item.productCatalogueItemUniqueReferenceNumber}::${item.productVariantUniqueReferenceNumber || item.variantUniqueReferenceNumber || ""}`,
            ),
        )
        .map(
          (item: any) =>
            ({
              ...(item as IProductCatalogue),
              productCatalogueItemUniqueReferenceNumber:
                item.productCatalogueItemUniqueReferenceNumber,
              variantUniqueReferenceNumber:
                item.productVariantUniqueReferenceNumber ||
                item.variantUniqueReferenceNumber,
              quantity: item.quantity || 1,
              name: item.productCatalogueName || item.name || "Product",
              price: item.price || 0,
              sku: item.sku || "",
              customerUniqueReferenceNumber:
                item.customerUniqueReferenceNumber || "",
              merchantUniqueReferenceNumber:
                item.merchantUniqueReferenceNumber || "",
              productImages:
                item.productImages && item.productImages.length
                  ? item.productImages
                  : item.productVariantImageUrl
                    ? [
                        {
                          imageType: "Primary",
                          uniqueReferenceNumber:
                            item.productVariantUniqueReferenceNumber || "",
                          documentVersion: "1",
                          documentAbsoluteUri: item.productVariantImageUrl,
                        },
                      ]
                    : [],
            }) as IPayLinkCartItem,
        );

      return [...prev, ...incoming];
    });
  }, [remoteCartItems]);

  React.useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  React.useEffect(() => {
    if (storeProfile) {
      localStorage.setItem(STORE_KEY, JSON.stringify(storeProfile));
      return;
    }

    localStorage.removeItem(STORE_KEY);
  }, [storeProfile]);

  const markPending = (productUrn: string, pending: boolean) => {
    setPendingProductUrns((prev) => {
      if (pending) {
        if (prev.includes(productUrn)) {
          return prev;
        }

        return [...prev, productUrn];
      }

      return prev.filter((urn) => urn !== productUrn);
    });
  };

  const addMutation = useMutation({
    mutationFn: addPayLinkStoreCartItem,
    onSuccess: () => {
      ToastMessage(TOASTER_TYPE.SUCCESS, "Product added to cart");
    },
  });
  const amendMutation = useMutation({
    mutationFn: amendPayLinkStoreCartItem,
  });
  const removeMutation = useMutation({
    mutationFn: removePayLinkStoreCartItem,
  });

  const setStoreProfile = (profile: IStoreProfile | null) => {
    setStoreProfileState(profile);
  };

  const addToCart = (product: IProductCatalogue) => {
    const productUrn = product.productCatalogueItemUniqueReferenceNumber;
    const customerUrn = product.customerUniqueReferenceNumber;
    const merchantSessionUrn = getOrCreateMerchantSessionUrn(customerUrn);

    const existingItem = cartItems.find(
      (item) => item.productCatalogueItemUniqueReferenceNumber === productUrn,
    );

    if (existingItem) {
      ToastMessage(TOASTER_TYPE.ERROR, "Product already exist in cart");
      return;
    }

    const prevState = [...cartItems];

    setCartItems((prev) => [
      ...prev,
      {
        ...product,
        uniqueReferenceNumber: merchantSessionUrn,
        quantity: 1,
      },
    ]);

    markPending(productUrn, true);

    const runAdd = (requestUrn: string) =>
      addMutation.mutateAsync({
        uniqueReferenceNumber: requestUrn,
        cartItemSessionUniqueReferenceNumber: cartSessionUrn,
        consumerUniqueReferenceNumber: "System",
        customerUniqueReferenceNumber: customerUrn,
        merchantUniqueReferenceNumber: product.merchantUniqueReferenceNumber,
        productCatalogueItemUniqueReferenceNumber:
          product.productCatalogueItemUniqueReferenceNumber,
        productVariantUniqueReferenceNumber:
          product.variantUniqueReferenceNumber,
        sku: product.sku,
        quantity: 1,
        productCatalogueItemName: product.name,
        productVariantImageUrl:
          product.productImages?.[0]?.documentAbsoluteUri ?? "",
        isSavedItem: false,
        cartItemAccessories: product.accessories ?? [],
        productCategoryUniqueReferenceNumber:
          product.categoryUniqueReferenceNumber,
        price: product.price,
      });

    runAdd(merchantSessionUrn)
      .catch(() => {
        setCartItems(prevState);
      })
      .finally(() => {
        markPending(productUrn, false);
      });
  };

  const incrementItem = (productUrn: string) => {
    const product = cartItems.find(
      (item) => item.productCatalogueItemUniqueReferenceNumber === productUrn,
    );

    if (!product) {
      return;
    }

    const existingItem = cartItems.find(
      (item) => item.productCatalogueItemUniqueReferenceNumber === productUrn,
    );

    if (existingItem) {
      // Product exists, just increment quantity locally
      const customerUrn = existingItem.customerUniqueReferenceNumber;
      const merchantSessionUrn = getOrCreateMerchantSessionUrn(customerUrn);

      const prevState = [...cartItems];

      setCartItems((prev) =>
        prev.map((item) =>
          item.productCatalogueItemUniqueReferenceNumber === productUrn
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );

      markPending(productUrn, true);

      amendMutation
        .mutateAsync({
          uniqueReferenceNumber: existingItem.uniqueReferenceNumber || "",
          cartItemSessionUniqueReferenceNumber: merchantSessionUrn,
          consumerUniqueReferenceNumber: "System",
          customerUniqueReferenceNumber: customerUrn,
          productCatalogueItemUniqueReferenceNumber: productUrn,
          quantity: existingItem.quantity + 1,
          merchantUniqueReferenceNumber:
            existingItem.merchantUniqueReferenceNumber,
          productCategoryUniqueReferenceNumber:
            existingItem.categoryUniqueReferenceNumber,
          productVariantUniqueReferenceNumber:
            existingItem.variantUniqueReferenceNumber,
          price: existingItem.price,
          sku: existingItem.sku,
          cartItemAccessories: [],
          productCatalogueItemName: existingItem.name,
          productVariantImageUrl:
            existingItem.productImages?.[0]?.documentAbsoluteUri ?? "",
        })
        .catch(() => {
          setCartItems(prevState);
        })
        .finally(() => {
          markPending(productUrn, false);
        });
      return;
    }

    addToCart(product);
  };

  const decrementItem = (productUrn: string) => {
    const target = cartItems.find(
      (item) => item.productCatalogueItemUniqueReferenceNumber === productUrn,
    );

    if (!target) {
      return;
    }

    if (target.quantity <= 1) {
      return;
    }

    const customerUrn = target.customerUniqueReferenceNumber;
    const merchantSessionUrn = getOrCreateMerchantSessionUrn(customerUrn);

    const prevState = [...cartItems];

    setCartItems((prev) =>
      prev.map((item) =>
        item.productCatalogueItemUniqueReferenceNumber === productUrn
          ? { ...item, quantity: item.quantity - 1 }
          : item,
      ),
    );

    markPending(productUrn, true);

    amendMutation
      .mutateAsync({
        uniqueReferenceNumber: target.uniqueReferenceNumber || "",
        cartItemSessionUniqueReferenceNumber: merchantSessionUrn,
        consumerUniqueReferenceNumber: "System",
        customerUniqueReferenceNumber: customerUrn,
        merchantUniqueReferenceNumber: target.merchantUniqueReferenceNumber,
        productCatalogueItemUniqueReferenceNumber:
          target.productCatalogueItemUniqueReferenceNumber,
        productVariantUniqueReferenceNumber:
          target.variantUniqueReferenceNumber,
        sku: target.sku,
        quantity: target.quantity - 1,
        productCatalogueItemName: target.name,
        productVariantImageUrl:
          target.productImages?.[0]?.documentAbsoluteUri ?? "",
        isSavedItem: false,
        cartItemAccessories: target.accessories ?? [],
        productCategoryUniqueReferenceNumber:
          target.categoryUniqueReferenceNumber,
        price: target.price,
      })
      .catch(() => {
        setCartItems(prevState);
      })
      .finally(() => {
        markPending(productUrn, false);
      });
  };

  const removeFromCart = (productUrn: string) => {
    const target = cartItems.find(
      (item) => item.productCatalogueItemUniqueReferenceNumber === productUrn,
    );

    if (!target) {
      return;
    }

    const customerUrn = target.customerUniqueReferenceNumber;
    const merchantSessionUrn = getOrCreateMerchantSessionUrn(customerUrn);

    const prevState = [...cartItems];
    const isLastCartItem = prevState.length === 1;

    setCartItems((prev) =>
      prev.filter(
        (item) => item.productCatalogueItemUniqueReferenceNumber !== productUrn,
      ),
    );

    markPending(productUrn, true);

    removeMutation
      .mutateAsync({
        uniqueReferenceNumber: merchantSessionUrn,
        consumerUniqueReferenceNumber: "System",
        customerUniqueReferenceNumber: customerUrn,
        merchantUniqueReferenceNumber: target.merchantUniqueReferenceNumber,
        productCatalogueItemUniqueReferenceNumber:
          target.productCatalogueItemUniqueReferenceNumber,
        productCatalogueVariantUniqueReferenceNumber:
          target.variantUniqueReferenceNumber,
      })
      .then(() => {
        // Reset sessions only when cart becomes empty to avoid breaking active items.
        if (!isLastCartItem) {
          return;
        }

        localStorage.removeItem(customerUrn);
        const nextCartSessionUrn = createFreshGlobalSessionUrn();
        setCartSessionUrn(nextCartSessionUrn);
      })
      .catch(() => {
        setCartItems(prevState);
      })
      .finally(() => {
        markPending(productUrn, false);
      });
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <PayLinkCartContext.Provider
      value={{
        storeProfile,
        setStoreProfile,
        cartItems,
        cartCount,
        pendingProductUrns,
        isFetchingCart,
        addToCart,
        incrementItem,
        decrementItem,
        removeFromCart,
        sessionUniqueReferenceNumber,
        setSessionUniqueReferenceNumber,
        merchantSessionUrn,
        cartSessionUrn,
        gatewaySetting,
        setGatewaySetting,
        currency: gatewaySetting?.currencySymbol ?? "",
      }}>
      {children}
    </PayLinkCartContext.Provider>
  );
};

export const usePayLinkCart = () => {
  const context = React.useContext(PayLinkCartContext);

  if (!context) {
    throw new Error("usePayLinkCart must be used within a PayLinkCartProvider");
  }

  return context;
};
